import ExcelJS from "exceljs";
import type { AssetType, ValueChainStage, AssetInput } from "@/lib/assets";

export const MAX_IMPORT_ROWS = 10;
export const OTHER_ASSET_TYPE_LABEL = "Other, please specify";

export const COLUMNS = [
  "Site name",
  "Asset type",
  "Custom asset type name",
  "Value chain stage",
  "Address",
  "Latitude",
  "Longitude",
  "City",
  "Country",
  "Business importance",
] as const;

type Column = (typeof COLUMNS)[number];

const BUSINESS_IMPORTANCE_OPTIONS = ["1 - Low", "2 - Medium", "3 - High", "4 - Very High"] as const;

// Columns whose presence signals "this row has real data" — used both to
// decide which template rows get pre-filled dropdown validation width and,
// on parse, to skip rows a user left entirely blank.
const REQUIRED_PRESENCE_COLUMNS: Column[] = ["Site name", "City", "Country", "Business importance"];

function columnIndex(header: Column): number {
  return COLUMNS.indexOf(header) + 1;
}

/** Hidden sheet holding one column of dropdown options per validated column. */
const LIST_SHEET_NAME = "Lists";

/**
 * Writes `options` down one column of the hidden list sheet and returns the
 * range formula referencing them.
 *
 * Dropdowns are range-based rather than inline (`"a,b,c"`) because Excel
 * splits an inline list on commas: OTHER_ASSET_TYPE_LABEL ("Other, please
 * specify") silently became two junk entries, "Other" and " please specify",
 * neither of which could ever match the exact string parseAssetImportFile
 * requires. A range holds each option as a discrete cell, so commas are just
 * characters — and it sidesteps Excel's ~255-character inline-formula limit
 * as a bonus.
 */
function writeOptionList(listSheet: ExcelJS.Worksheet, column: number, options: string[]): string | null {
  if (options.length === 0) return null;

  options.forEach((option, i) => {
    listSheet.getCell(i + 1, column).value = option;
  });

  // LIST_SHEET_NAME has no spaces, so it needs no quoting in the reference.
  const letter = listSheet.getColumn(column).letter;
  return `=${LIST_SHEET_NAME}!$${letter}$1:$${letter}$${options.length}`;
}

function setListValidation(sheet: ExcelJS.Worksheet, rowNumber: number, header: Column, formula: string | null) {
  if (!formula) return;
  sheet.getCell(rowNumber, columnIndex(header)).dataValidation = {
    type: "list",
    allowBlank: true,
    formulae: [formula],
  };
}

export async function buildAssetImportTemplateWorkbook(
  assetTypes: AssetType[],
  stages: ValueChainStage[],
): Promise<ExcelJS.Workbook> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Assets");

  sheet.columns = COLUMNS.map((header) => ({ header, key: header, width: 26 }));
  sheet.getRow(1).font = { bold: true };

  const listSheet = workbook.addWorksheet(LIST_SHEET_NAME);
  // "veryHidden" keeps it out of Excel's own unhide dialog, so a client
  // can't accidentally edit the option lists out from under the parser.
  listSheet.state = "veryHidden";

  const assetTypeFormula = writeOptionList(listSheet, 1, [...assetTypes.map((t) => t.name), OTHER_ASSET_TYPE_LABEL]);
  const stageFormula = writeOptionList(listSheet, 2, stages.map((s) => s.name));
  const importanceFormula = writeOptionList(listSheet, 3, [...BUSINESS_IMPORTANCE_OPTIONS]);

  for (let row = 2; row <= MAX_IMPORT_ROWS + 1; row++) {
    setListValidation(sheet, row, "Asset type", assetTypeFormula);
    setListValidation(sheet, row, "Value chain stage", stageFormula);
    setListValidation(sheet, row, "Business importance", importanceFormula);
  }

  return workbook;
}

export async function downloadAssetImportTemplate(assetTypes: AssetType[], stages: ValueChainStage[]): Promise<void> {
  const workbook = await buildAssetImportTemplateWorkbook(assetTypes, stages);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "asset-import-template.xlsx";
  link.click();
  URL.revokeObjectURL(url);
}

export interface ParsedAssetRow {
  row: number;
  asset: AssetInput;
}

export interface ImportRowError {
  row: number;
  message: string;
}

export type ImportParseResult = { ok: true; rows: ParsedAssetRow[] } | { ok: false; errors: ImportRowError[] };

function fileError(message: string): ImportParseResult {
  return { ok: false, errors: [{ row: 0, message }] };
}

function cellText(row: ExcelJS.Row, columnByHeader: Map<Column, number>, header: Column): string {
  const col = columnByHeader.get(header);
  if (!col) return "";
  const value = row.getCell(col).value;
  if (value && typeof value === "object") {
    // Excel applies rich text to a cell any time it has run-level formatting
    // (e.g. pasted from elsewhere, partially bolded) — a real occurrence,
    // not an exotic edge case, so both cell-value shapes need handling:
    if ("richText" in value) {
      return (value as { richText: { text: string }[] }).richText.map((r) => r.text).join("").trim();
    }
    if ("text" in value) {
      return String((value as { text: unknown }).text ?? "").trim();
    }
  }
  return String(value ?? "").trim();
}

/**
 * Every accepted spelling of an importance level, lowercased. Two shapes are
 * valid: the template's own dropdown label ("3 - High"), and the bare digit
 * — a client who types 3 into the cell rather than picking from the dropdown
 * gets back the number 3, which `cellText` stringifies to "3".
 *
 * This is an exact-match lookup, deliberately. The previous check read only
 * the cell's FIRST CHARACTER, so "10" silently imported as importance 1 and
 * "4.9" as 4 — a wrong business-importance value feeds straight into
 * combineVulnerability() and therefore into the client's risk score, so it
 * has to fail loudly rather than be coerced into something plausible.
 */
const BUSINESS_IMPORTANCE_BY_TEXT = new Map<string, 1 | 2 | 3 | 4>([
  ["1", 1],
  ["2", 2],
  ["3", 3],
  ["4", 4],
  ...BUSINESS_IMPORTANCE_OPTIONS.map(
    (label, i) => [label.toLowerCase(), (i + 1) as 1 | 2 | 3 | 4] as const,
  ),
]);

function parseBusinessImportance(text: string): 1 | 2 | 3 | 4 | null {
  return BUSINESS_IMPORTANCE_BY_TEXT.get(text.trim().toLowerCase()) ?? null;
}

function validateRow(
  rowNumber: number,
  row: ExcelJS.Row,
  columnByHeader: Map<Column, number>,
  assetTypes: AssetType[],
  stages: ValueChainStage[],
): { asset: AssetInput } | { errors: ImportRowError[] } {
  const errors: string[] = [];
  const get = (header: Column) => cellText(row, columnByHeader, header);

  const name = get("Site name");
  const assetTypeName = get("Asset type");
  const customAssetTypeNameText = get("Custom asset type name");
  const valueChainStageName = get("Value chain stage");
  const address = get("Address");
  const latitudeText = get("Latitude");
  const longitudeText = get("Longitude");
  const city = get("City");
  const country = get("Country");
  const businessImportanceText = get("Business importance");

  if (!name) errors.push("Site name is required");
  if (!city) errors.push("City is required");
  if (!country) errors.push("Country is required");

  let assetTypeId: string | null = null;
  let customAssetTypeName: string | null = null;
  if (!assetTypeName) {
    errors.push("Asset type is required");
  } else if (assetTypeName === OTHER_ASSET_TYPE_LABEL) {
    if (!customAssetTypeNameText) {
      errors.push('Custom asset type name is required when Asset type is "Other, please specify"');
    }
    customAssetTypeName = customAssetTypeNameText || null;
  } else {
    const match = assetTypes.find((t) => t.name === assetTypeName);
    if (!match) {
      errors.push(`"${assetTypeName}" is not a recognized asset type`);
    } else {
      assetTypeId = match.id;
    }
  }

  const matchedType = assetTypeId ? assetTypes.find((t) => t.id === assetTypeId) : undefined;
  const needsManualStage = assetTypeName === OTHER_ASSET_TYPE_LABEL || (!!matchedType && !matchedType.valueChainStageId);
  let valueChainStageId: string | null = null;
  if (needsManualStage) {
    if (!valueChainStageName) {
      errors.push("Value chain stage is required for this asset type");
    } else {
      const stageMatch = stages.find((s) => s.name === valueChainStageName);
      if (!stageMatch) {
        errors.push(`"${valueChainStageName}" is not a recognized value chain stage`);
      } else {
        valueChainStageId = stageMatch.id;
      }
    }
  } else if (matchedType?.valueChainStageId) {
    valueChainStageId = matchedType.valueChainStageId;
  }

  const hasAddress = !!address;
  const latitude = latitudeText ? Number(latitudeText) : null;
  const longitude = longitudeText ? Number(longitudeText) : null;
  const hasCoords = latitude !== null && longitude !== null && !Number.isNaN(latitude) && !Number.isNaN(longitude);
  if (!hasAddress && !hasCoords) {
    errors.push("Provide an Address, or both Latitude and Longitude");
  } else if ((latitudeText && !longitudeText) || (!latitudeText && longitudeText)) {
    errors.push("Latitude and Longitude must both be provided together");
  }

  // Blank and invalid are reported separately: "you left it out" and "10 is
  // not a level" are different mistakes to fix, and the invalid case has to
  // name the offending value or the client can't tell which cell is wrong.
  const businessImportance = parseBusinessImportance(businessImportanceText);
  if (!businessImportanceText) {
    errors.push("Business importance is required");
  } else if (!businessImportance) {
    errors.push(
      `"${businessImportanceText}" is not a valid Business importance — use one of ` +
        BUSINESS_IMPORTANCE_OPTIONS.map((label) => `"${label}"`).join(", "),
    );
  }

  if (errors.length > 0) {
    return { errors: errors.map((message) => ({ row: rowNumber, message })) };
  }

  return {
    asset: {
      name,
      assetTypeId,
      customAssetTypeName,
      valueChainStageId: needsManualStage ? valueChainStageId : null,
      address: hasAddress ? address : null,
      latitude: hasCoords ? latitude : null,
      longitude: hasCoords ? longitude : null,
      city,
      country,
      businessImportance: businessImportance as 1 | 2 | 3 | 4,
    },
  };
}

export async function parseAssetImportFile(
  fileName: string,
  buffer: ArrayBuffer,
  assetTypes: AssetType[],
  stages: ValueChainStage[],
): Promise<ImportParseResult> {
  if (!fileName.toLowerCase().endsWith(".xlsx")) {
    return fileError("Please upload the .xlsx template file.");
  }

  let workbook: ExcelJS.Workbook;
  try {
    workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
  } catch {
    return fileError("Couldn't read this file — make sure it's the unmodified .xlsx template with your data filled in.");
  }

  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return fileError("The file has no worksheet.");
  }

  const columnByHeader = new Map<Column, number>();
  sheet.getRow(1).eachCell((cell, colNumber) => {
    const header = String(cell.value ?? "").trim();
    if ((COLUMNS as readonly string[]).includes(header)) {
      columnByHeader.set(header as Column, colNumber);
    }
  });

  const missingColumns = COLUMNS.filter((header) => !columnByHeader.has(header));
  if (missingColumns.length > 0) {
    return { ok: false, errors: missingColumns.map((header) => ({ row: 1, message: `Missing column: ${header}` })) };
  }

  // sheet.rowCount can include rows the template touched only to attach
  // dropdown validation to (Task 5) even though the user never typed
  // anything into them — filter to rows with real data before applying the
  // row cap, not just "every row exceljs happens to report."
  const dataRows: { rowNumber: number; row: ExcelJS.Row }[] = [];
  for (let rowNumber = 2; rowNumber <= sheet.rowCount; rowNumber++) {
    const row = sheet.getRow(rowNumber);
    const isBlank = REQUIRED_PRESENCE_COLUMNS.every((header) => !cellText(row, columnByHeader, header));
    if (!isBlank) dataRows.push({ rowNumber, row });
  }

  if (dataRows.length === 0) {
    return fileError("The template has no data rows filled in.");
  }
  if (dataRows.length > MAX_IMPORT_ROWS) {
    return fileError(`This template accepts at most ${MAX_IMPORT_ROWS} sites per upload (found ${dataRows.length}).`);
  }

  const errors: ImportRowError[] = [];
  const rows: ParsedAssetRow[] = [];
  for (const { rowNumber, row } of dataRows) {
    const result = validateRow(rowNumber, row, columnByHeader, assetTypes, stages);
    if ("errors" in result) {
      errors.push(...result.errors);
    } else {
      rows.push({ row: rowNumber, asset: result.asset });
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true, rows };
}
